/**
 * Shared MSAL Instance - Single PublicClientApplication with proper v3 initialization
 *
 * MSAL Browser v3 requires explicit initialization via await initialize()
 * before any MSAL APIs can be called. This module ensures a single shared
 * instance is created and initialized once.
 */

import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "./config";

let msalInstance = null;
let initializePromise = null;

/**
 * Get the initialized MSAL instance (async - guaranteed initialized).
 * Creates and initializes the instance on first call, returns cached instance after.
 * @returns {Promise<PublicClientApplication>} The initialized MSAL instance
 */
export async function getMsalInstance() {
  if (msalInstance) {
    return msalInstance;
  }

  // Prevent multiple parallel initializations
  if (!initializePromise) {
    initializePromise = createAndInitialize();
  }

  return initializePromise;
}

/**
 * Get the MSAL instance synchronously. Returns null if not yet initialized.
 * Use this only for non-critical sync checks (e.g. computed properties).
 * @returns {PublicClientApplication|null}
 */
export function getMsalInstanceSync() {
  return msalInstance;
}

async function createAndInitialize() {
  const instance = new PublicClientApplication(msalConfig);
  await instance.initialize();
  msalInstance = instance;
  console.log("[MSAL] PublicClientApplication initialized (v3 pattern)");
  return msalInstance;
}
